import SignUpUserInfo, { type SignUpUserInfoData } from "@/components/Molecules/SignUpUserInfo";
import { useState } from "react";
import SignUpUserDetails, { type SignUpUserDetailsData } from "@/components/Molecules/SignUpUserDetails";
import useSessionContext from "@/hooks/useSesionContext";
import Analytics from "@/utils/Analytics";
import { createUser } from "@/services/auth";
import { useMutation } from "@tanstack/react-query";
import { type AuthStep } from "@/components/Navbar/AuthModal/types";
import { signUp as signUpAnalytics } from "@utils/analyticsV2";

import useModalHearAboutUs from "@/hooks/useModalHearAboutUs";
import useGoogleReCaptchaV3 from "@/hooks/useGoogleReCaptchaV3";
import { parseError } from "@/utils/errorHandling";

interface SignUpProps {
  onClose: () => void;
  isMobile?: boolean;
  updateCurrentUser: (data: Omit<UserAttr, "token">) => void;
  setStep: React.Dispatch<React.SetStateAction<AuthStep>>;
  tokenUserRegistration: string;
}
export interface SignUpData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  // hear_about_us_source: string;
  identification: string;
  identification_type: string;
}

const SignUp = ({ onClose, updateCurrentUser, setStep, tokenUserRegistration }: SignUpProps) => {
  const { setUserToken } = useSessionContext();
  const { handleOpenModalHearAboutUs } = useModalHearAboutUs();

  const [stepSingUp, setStepSignUp] = useState<0 | 1>(0);
  const [date, setData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    // hear_about_us_source: "",
    identification: "",
    identification_type: "",
  });
  const [fetchError, setFetchError] = useState<string[] | string>([]);
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);

  const { executeReCaptcha } = useGoogleReCaptchaV3({
    shouldLoad: true,
  });

  const signUp = useMutation({
    mutationFn: createUser,
    onSuccess: async (data) => {
      updateCurrentUser(data);
      setUserToken(data.token);
      handleOpenModalHearAboutUs("registration");
      onClose();
      // Identify  in sessionContext

      void Analytics.CompleteRegistration({
        id: data.id,
        user: {
          email: data.email,
          firstName: `${data.first_name}`,
          lastName: `${data.last_name}`,
          phone: data.telephone,
          createdAt: data.date_joined,
        },
      });
    },
    onError: (error: any) => {
      if (error) {
        setFetchError(parseError(error));
        setTimeout(() => {
          setFetchError([]);
        }, 5000);
      }
    },
  });

  const handleSubmitUserInfo = (data: SignUpUserInfoData) => {
    setStepSignUp(1);
    setData((prev) => ({ ...prev, email: data.email, password: data.password }));
  };

  const handleSubmitUserDetails = async (data: SignUpUserDetailsData) => {
    if (isSubmittingDetails) return;
    setIsSubmittingDetails(true);
    setData((prev) => ({
      ...prev,
      first_name: data.first_name,
      last_name: data.last_name,
      identification: data.identification,
      identification_type: data.identification_type,
    }));

    try {
      if (data) {
        const recaptchaToken = await executeReCaptcha("registration");
        const response = await signUp.mutateAsync({
          email: date.email,
          password: date.password,
          firstName: data.first_name,
          lastName: data.last_name,
          identification: data.identification,
          identificationType: data.identification_type,
          token: tokenUserRegistration,
          recaptchaToken,
        });

        signUpAnalytics.eventUserInfo({
          method: "telephone",
          userId: response ? String(response.id) : undefined,
        });
      }
    } finally {
      setIsSubmittingDetails(false);
    }
  };

  function backStepAuth() {
    setStep("login");
  }

  function backStep() {
    setStepSignUp(0);
  }
  return (
    <>
      {stepSingUp === 0 && (
        <SignUpUserInfo onClose={onClose} onSubmit={handleSubmitUserInfo} backStep={backStepAuth} />
      )}
      {stepSingUp === 1 && (
        <SignUpUserDetails
          onClose={onClose}
          onSubmit={handleSubmitUserDetails}
          fetchingError={fetchError}
          backStep={backStep}
          loading={isSubmittingDetails || signUp.isPending}
        />
      )}
    </>
  );
};

export default SignUp;
